import NextLink from 'next/link';
import type { ReactNode } from 'react';
import assert from '@/modules/utils/assert';
import type { Selfcare, Sidepanel } from '@/payload/types';
import { getLinkUrl } from '@/payload/utils/getRichLinkUrl';
import styles from './Link.module.scss';

export type Link = {
	linkType: 'custom' | 'internal' | 'sidepanel' | 'contact';
	newTab?: boolean;
	url?: string;
	doc?: {
		relationTo: string;
		value: number | Selfcare;
	};
	sidepanel?: number | Sidepanel;
};

export interface RichTextLinkProps {
	link?: Link;
	children: ReactNode;
	className?: string;
}

export default function RichTextLink(props: RichTextLinkProps) {
	const { link, children, className = styles.link } = props;

	if (!link) return null;

	if (link.linkType === 'sidepanel') {
		return <SidepanelLink {...props} />;
	}

	const linkUrl = getLinkUrl(link);
	const target = link.newTab ? '_blank' : undefined;
	const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

	return (
		<NextLink className={className} href={linkUrl} target={target} rel={rel} scroll={true}>
			{children}
		</NextLink>
	);
}

function SidepanelLink(props: RichTextLinkProps) {
	const { link, children, className = styles.link } = props;

	try {
		assert(link?.linkType === 'sidepanel', `link.type should be sidepanel, but is: ${link?.linkType}`);
		assert(link.sidepanel !== null, 'link.sidepanel should not be null');
		assert(link.sidepanel !== undefined, 'link.sidepanel should not be undefined');
		assert(typeof link.sidepanel === 'object');
		assert(typeof link.sidepanel.slug === 'string', 'link.sidepanel.value.slug should be a string');
	} catch (error) {
		console.error(error);
		return null;
	}

	const sidepanel = link.sidepanel;
	const href = `?spc=${sidepanel.slug}`;

	return (
		<NextLink
			className={className}
			href={href}
			onClick={(event) => {
				event.preventDefault();
				window.history.pushState(null, '', href);
			}}
			scroll={false}
		>
			{children}
		</NextLink>
	);
}
